/**
 * Centralized debug logging utility
 *
 * Controls console output based on environment and debug flags
 * Prevents console spam in production and during development
 */

const IS_DEV = import.meta.env.DEV
const DEBUG_ENABLED = localStorage?.getItem('DEBUG_MODE') === 'true'

/** Enable verbose auth/initial-load logs (supabase storage, AuthContext, PublicRoute, ActiveContext, Onboarding). Set VITE_DEBUG_AUTH=true in .env to debug login flow. */
export function isAuthDebugEnabled(): boolean {
  return IS_DEV && import.meta.env.VITE_DEBUG_AUTH === 'true'
}

// Specific debug categories (enable/disable per feature)
const DEBUG_CATEGORIES = {
  auth: false,
  theme: false,
  protectedRoute: false,
  analytics: false,
  analyzer: true,
  testing: false,
  api: true,
  activeContext: false, // Disable noisy context switching logs
  header: false, // Disable header re-render logs
  storage: false, // Disable storage system logs
}

type DebugCategory = keyof typeof DEBUG_CATEGORIES

/**
 * Debug log - only outputs if category is enabled
 */
export function debugLog(category: DebugCategory, ...args: unknown[]): void {
  if (!IS_DEV && !DEBUG_ENABLED) return
  if (!DEBUG_CATEGORIES[category]) return

  console.log(`[${category}]`, ...args)
}

/**
 * Debug warn - always shows in dev, respects category in production
 */
export function debugWarn(category: DebugCategory, ...args: unknown[]): void {
  if (!IS_DEV && !DEBUG_ENABLED) return
  if (!DEBUG_CATEGORIES[category]) return

  console.warn(`[${category}]`, ...args)
}

/**
 * Debug error - always shows regardless of category
 */
export function debugError(category: DebugCategory, ...args: unknown[]): void {
  console.error(`[${category}]`, ...args)
}

/**
 * Enable debug logging for a specific category at runtime
 * Usage in console: window.enableDebug('theme')
 */
export function enableDebugCategory(category: DebugCategory): void {
  DEBUG_CATEGORIES[category] = true
  console.log(`✓ Debug enabled for: ${category}`)
}

/**
 * Disable debug logging for a specific category
 */
export function disableDebugCategory(category: DebugCategory): void {
  DEBUG_CATEGORIES[category] = false
  console.log(`✗ Debug disabled for: ${category}`)
}

/**
 * Show current debug configuration
 */
export function showDebugConfig(): void {
  console.table(DEBUG_CATEGORIES)
}

// Expose to window for runtime debugging
if (typeof window !== 'undefined') {
  ;(window as any).enableDebug = enableDebugCategory
  ;(window as any).disableDebug = disableDebugCategory
  ;(window as any).showDebug = showDebugConfig
}
